import { decodeToken } from "./tokenUtils";

/**
 * Company IDs whose members see the reporter (report) view in the case-viewer
 * instead of the reviewer disposition panel — even when their `user_type` is
 * `reviewer`.
 *
 * HT Médica (`ht-medica`) is a customer pilot: they consume AI findings as a
 * report, not as a QA agree/disagree disposition queue. Their accounts stay
 * `user_type: reviewer` because that role is what grants cohort-scoped data
 * access; only the *view* changes. Add a `company_id` here to opt its
 * reviewers into the report view.
 *
 * Single source of truth so widening the set (or the surfaces that honour it)
 * is a one-line change.
 */
export const REPORTER_VIEW_COMPANY_IDS: ReadonlySet<string> = new Set(["ht-medica"]);

/**
 * The `company_id` claim from a JWT access token, or null when the token is
 * missing, undecodable, or carries no (string) company_id. Mirrors how
 * `reviewerNav.ts` reads `allowed_cohorts` off the token.
 */
export const companyIdFromToken = (token: string | null | undefined): string | null => {
  if (!token) return null;
  const claims = decodeToken(token);
  return typeof claims?.company_id === "string" ? claims.company_id : null;
};

/**
 * Whether the case-viewer should render the reviewer disposition panel
 * (`ReviewerPanel`) for this user. Returns false — i.e. fall through to the
 * report view — for every non-reviewer role (they already report-view) and for
 * reviewers whose company is opted into the reporter view.
 */
export const shouldShowReviewerPanel = (opts: {
  userType: string | null | undefined;
  companyId: string | null | undefined;
}): boolean => {
  if (opts.userType !== "reviewer") return false;
  return !(opts.companyId != null && REPORTER_VIEW_COMPANY_IDS.has(opts.companyId));
};
